import { useEffect, useRef } from "react";

/**
 * Landskapet bak siden.
 *
 * Appen handler om hvordan Norge ser ut, og møtte deg med en kremfarget flate.
 * Dette er de fire tingene som faktisk kjennetegner en fjordutsikt, lagvis
 * bakfra og fram: dis over de fjerneste ryggene, snø på de midterste, to bratte
 * vegger som rammer inn utsikten, og vann som ligger stille mellom dem.
 *
 * Lagene flytter seg i ulik takt når man ruller. Det er ikke pynt for pyntens
 * skyld — parallaksen er det som gjør at avstand leses som avstand: den
 * fjerneste ryggen skal nesten stå stille, veggen nærmest deg skal skyve seg
 * raskt ut av bildet. Uten forskjellen i fart er det bare et bilde.
 *
 * Fire ting styrer hvordan dette er bygget:
 *
 *   Den skal aldri klippes. Scenen ligger fast mot vinduet og dekker det helt,
 *   i alle rulleposisjoner. Et tidligere forsøk lot den ligge i toppen av
 *   dokumentet og rulle bort, og da endte den i en synlig kant tvers over
 *   skjermen — et bilde som så ut som det ikke ble ferdig lastet.
 *
 *   Den skal fortsette å svare når man ruller. Samme forsøk frøs bevegelsen
 *   etter én skjermhøyde. Her dempes forskyvningen mot et tak i stedet, så
 *   lagene beveger seg gjennom hele den delen av siden der man kan se dem.
 *
 *   Lesbarhet. Fjellene toner ned bak innholdet etter hvert som man ruller —
 *   `--veil` under. Et landskap i full styrke bak brødteksten er tekst man må
 *   anstrenge seg for å lese, og denne appen er lange tekster om steder.
 *
 *   Ytelse. Én `scroll`-lytter, passiv, som skriver to tall til CSS-variabler
 *   inne i en `requestAnimationFrame`. Lagene flytter seg med `translate3d`,
 *   som kompositoren gjør uten å regne layout på nytt.
 *
 *   Bevegelse man ikke har bedt om. `prefers-reduced-motion` slår av
 *   parallaksen. Scenen blir stående som et stille bilde — og sløret legges
 *   likevel på, ellers ville fjellene stått i full styrke bak all teksten.
 */

/**
 * Hvor raskt hvert lag synker ut av bildet når man ruller.
 *
 * Scenen ligger fast mot vinduet, så uten forskyvning ville den stått bom
 * stille mens innholdet gled forbi. Tallet dytter laget nedover: 0 står
 * stille — uendelig langt unna — og 1 følger siden nøyaktig, som om det lå
 * i dokumentet. Alt her er imellom, og de nærmeste lagene har de høyeste
 * tallene, slik man ser det fra et tog.
 */
const LAYERS = [
  { key: "sky", rate: 0 },
  { key: "glow", rate: 0.08 },
  { key: "far", rate: 0.22 },
  { key: "mid", rate: 0.4 },
  { key: "water", rate: 0.6 },
  { key: "near", rate: 0.78 },
] as const;

/**
 * Taket for forskyvningen, som andel av vindushøyden.
 *
 * Uten et tak skyver det nærmeste laget seg tusenvis av piksler ned på en lang
 * side, og nettleseren holder et enormt lag i live utenfor bildet. Med taket
 * er alt ute av syne lenge før det nås — men bevegelsen fryser ikke midt på
 * første skjerm, som den gjorde med et tak på nøyaktig én skjermhøyde.
 */
const DRIFT_LIMIT = 2.2;

/**
 * Hvor langt man må rulle før fjellene er tonet helt ned bak innholdet.
 *
 * Trekvart skjermhøyde: da er toppseksjonen i ferd med å forlate bildet, og
 * det som kommer inn nedenfra er kort og tekst som skal kunne leses.
 */
const VEIL_AT = 0.75;

export function FjordBackdrop() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = root.current;
    if (!element) return;

    /*
     * Den som har slått av bevegelse får scenen stående — men den får
     * fortsatt sløret, som bare er en gjennomsiktighet og ikke en bevegelse.
     * Se `--veil` i `paint`.
     */
    const still =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let frame = 0;
    const paint = () => {
      frame = 0;
      const height = window.innerHeight || 1;
      const y = window.scrollY;

      /*
       * Forskyvningen dempes mot taket framfor å klippes ved det. `min` ville
       * gitt et merkbart stopp i det man passerte grensa; dette gir en
       * bevegelse som avtar, og som aldri helt stanser.
       */
      const limit = height * DRIFT_LIMIT;
      const drift = still ? 0 : limit * (1 - Math.exp(-y / limit));
      element.style.setProperty("--sy", drift.toFixed(2));

      /*
       * Sløret settes uansett, også uten bevegelse.
       *
       * `prefers-reduced-motion` betyr «ikke flytt ting», ikke «la fjellene
       * stå i full styrke bak brødteksten». Å tone dem ned er ikke bevegelse
       * — det er det som gjør teksten lesbar, og det trenger alle.
       */
      element.style.setProperty("--veil", Math.min(y / (height * VEIL_AT), 1).toFixed(3));
    };

    const onScroll = () => {
      // Én oppdatering per bilde. `scroll` fyrer langt oftere enn skjermen
      // tegner, og uten dette regnes den samme posisjonen ut flere ganger.
      if (frame === 0) frame = requestAnimationFrame(paint);
    };

    paint();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div className="fjord no-print" ref={root} aria-hidden="true">
      {LAYERS.map(({ key, rate }) => (
        <div
          key={key}
          className={`fjord-layer fjord-layer--${key}`}
          style={{ "--rate": rate } as React.CSSProperties}
        >
          {SCENERY[key]}
        </div>
      ))}
      {/*
        Sløret som toner landskapet ned bak innholdet, og sømmen som gjør at
        bunnen av bildet alltid er sidefargen. Se styles.css for begge.
      */}
      <div className="fjord-veil" />
      <div className="fjord-hem" />
    </div>
  );
}

/**
 * Selve fjellene.
 *
 * Tegnet for hånd framfor hentet som bilde: et fotografi ville kostet
 * hundrevis av kilobyte, sett feil ut i mørk modus, og krevd kreditering. Disse
 * er noen få hundre byte hver, følger temafargene gjennom `currentColor`, og
 * skalerer til enhver skjerm.
 *
 * `preserveAspectRatio="none"` på ryggene er med vilje. De skal strekke seg
 * over hele bredden uansett vindu — en fjellrygg som beholder sideforholdet
 * ville etterlatt tomme hjørner på en bred skjerm.
 */
const SCENERY: Record<(typeof LAYERS)[number]["key"], React.ReactNode> = {
  /* Himmelen er ren CSS — en gradient trenger ingen SVG. */
  sky: null,

  /*
   * Lyset. Midnattssol i lyst tema, nordlys i mørkt — begge er ekte norske
   * himler, og hvilken av dem som passer, avhenger av temaet under.
   */
  glow: (
    <svg viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice">
      <defs>
        <radialGradient id="fjord-sun" cx="0.72" cy="0.62" r="0.55">
          <stop offset="0%" stopColor="var(--fjord-glow-core)" />
          <stop offset="55%" stopColor="var(--fjord-glow-mid)" />
          <stop offset="100%" stopColor="var(--fjord-glow-edge)" />
        </radialGradient>
      </defs>
      <rect width="1440" height="600" fill="url(#fjord-sun)" />
      {/*
        Nordlysbåndet. Ligger her i begge temaer, men er gjennomsiktig i lyst —
        se `--fjord-aurora` i styles.css. To bånd, ikke ett: nordlys henger
        sjelden som en enkelt strek.
      */}
      <path
        className="fjord-aurora fjord-aurora--a"
        d="M-100 210 C 220 120, 430 250, 700 170 S 1180 90, 1560 180 L 1560 250 C 1180 165, 950 300, 700 235 S 220 195, -100 280 Z"
      />
      <path
        className="fjord-aurora fjord-aurora--b"
        d="M-100 300 C 260 235, 520 340, 780 275 S 1240 215, 1560 290 L 1560 335 C 1240 265, 1010 375, 780 325 S 260 290, -100 355 Z"
      />
    </svg>
  ),

  /* Den fjerneste ryggen. Lav, myk, nesten oppslukt av disen. */
  far: (
    <svg viewBox="0 0 1440 240" preserveAspectRatio="none">
      <path d="M0 240 L0 150 L90 118 L180 142 L280 96 L390 134 L500 104 L610 138 L720 92 L840 130 L960 100 L1080 140 L1190 108 L1300 138 L1380 116 L1440 140 L1440 240 Z" />
    </svg>
  ),

  /*
   * De midterste toppene, med snø. Snøen er egne stier over selve fjellet, så
   * den ligger der den faktisk ligger — på toppene, ikke som et jevnt lag.
   */
  mid: (
    <svg viewBox="0 0 1440 320" preserveAspectRatio="none">
      <path d="M0 320 L0 196 L120 132 L240 188 L360 78 L470 150 L580 104 L700 176 L820 60 L940 148 L1060 96 L1180 170 L1300 118 L1440 178 L1440 320 Z" />
      <g className="fjord-snow">
        <path d="M360 78 L392 100 L376 104 L352 92 L336 102 L320 96 Z" />
        <path d="M820 60 L856 84 L836 90 L812 76 L792 88 L776 80 Z" />
        <path d="M1060 96 L1088 116 L1072 120 L1052 110 L1036 118 L1022 112 Z" />
      </g>
    </svg>
  ),

  /*
   * Vannet. Stripene er speilinger, ikke bølger — en fjord i stille vær ligger
   * som et speil, og det er den flaten som gjør at bildet leses som fjord og
   * ikke som en dal.
   */
  water: (
    <svg viewBox="0 0 1440 260" preserveAspectRatio="none">
      <rect width="1440" height="260" className="fjord-water-body" />
      <g className="fjord-shimmer">
        <rect x="120" y="46" width="360" height="3" rx="1.5" />
        <rect x="620" y="72" width="520" height="3" rx="1.5" />
        <rect x="260" y="108" width="280" height="2" rx="1" />
        <rect x="780" y="140" width="420" height="2" rx="1" />
        <rect x="80" y="178" width="300" height="2" rx="1" />
      </g>
    </svg>
  ),

  /*
   * Veggene nærmest. To bratte sider som rammer inn utsikten — det er nettopp
   * dette som skiller en fjord fra en innsjø, og det som gir hele scenen dybde.
   * De er mørkest, fordi det som er nær ligger i skygge når lyset kommer bakfra.
   */
  near: (
    <svg viewBox="0 0 1440 420" preserveAspectRatio="none">
      <path d="M0 420 L0 40 L70 96 L128 66 L196 150 L250 128 L318 232 L360 210 L410 300 L448 288 L470 420 Z" />
      <path d="M1440 420 L1440 20 L1364 84 L1300 52 L1228 144 L1170 118 L1096 230 L1050 206 L996 302 L954 290 L930 420 Z" />
    </svg>
  ),
};
